import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import ToolSeoHero from "../components/tools-seo/ToolSeoHero";
import ToolSeoBenefits from "../components/tools-seo/ToolSeoBenefits";
import ToolSeoFAQ from "../components/tools-seo/ToolSeoFAQ";
import ToolSeoCTA from "../components/tools-seo/ToolSeoCTA";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, { buildFAQSchema } from "../components/seo/StructuredData";

const THEME_KEY = "hookora_theme";

const descriptionFaqs = [
  {
    question: "What is a description rewriter?",
    answer:
      "A description rewriter is a tool that helps improve written descriptions by making them clearer, stronger, and easier to understand."
  },
  {
    question: "Who is Hookora’s Description Rewriter for?",
    answer:
      "It is built for creators, marketers, personal brands, and anyone who wants a faster way to improve content descriptions."
  },
  {
    question: "Why do descriptions matter?",
    answer:
      "Descriptions help explain your content, frame the value, and support stronger packaging. A better description makes your content feel more complete."
  },
  {
    question: "Can I use this for YouTube descriptions?",
    answer:
      "Yes. This tool is useful for YouTube descriptions, social post descriptions, and other creator workflows where message clarity matters."
  },
  {
    question: "Does Hookora only rewrite descriptions?",
    answer:
      "No. Hookora also includes tools for hooks, titles, captions, hashtags, scripts, and thumbnail text."
  },
  {
    question: "Is this useful if I already wrote something?",
    answer:
      "Yes. That is one of the main use cases. Hookora helps improve existing wording so you do not have to start over every time."
  }
];

function DescriptionRewriterPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const pageStyle = useMemo(
    () => ({
      background: isDark ? "#020617" : "#f8fafc",
      backgroundImage: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      minHeight: "100vh"
    }),
    [isDark]
  );

  return (
    <div style={pageStyle}>
      <SeoHead
        title="Description Rewriter | Hookora"
        description="Use Hookora’s Description Rewriter to improve clarity, structure, and content positioning faster for videos, posts, and creator workflows."
        path="/description-rewriter"
      />
      <StructuredData
        id="description-rewriter-faq-schema"
        data={buildFAQSchema(descriptionFaqs)}
      />

      <Navbar />
      <ToolSeoHero
        badge="Hookora Tool • Description Rewriter"
        title="Description Rewriter for creators who want clearer, stronger content positioning"
        description="The Hookora Description Rewriter helps creators improve content descriptions without rewriting everything from scratch. Instead of struggling to make your message sound clearer, you get stronger description directions that support clarity, structure, and better overall packaging."
        highlights={[
          "Rewrite faster",
          "Improve clarity",
          "Strengthen positioning",
          "Built for creator workflow"
        ]}
        primaryCtaLabel="Start Free"
        primaryCtaTo="/register"
        secondaryCtaLabel="See Features"
        secondaryCtaTo="/features"
        previewTitle="What the Description Rewriter helps you do"
        previewItems={[
          {
            label: "Clarity",
            text: "Turn weak or messy descriptions into wording that feels easier to understand."
          },
          {
            label: "Structure",
            text: "Improve how your description flows so the message feels more intentional."
          },
          {
            label: "Packaging",
            text: "Make your content presentation feel stronger without starting from zero."
          }
        ]}
      />

      <ToolSeoBenefits
        eyebrow="Why creators use it"
        title="A description rewriter should help your content feel clearer, not just longer"
        description="Descriptions matter because they help explain what your content is about, what value it offers, and why someone should care. Hookora helps make that message stronger and easier to shape."
        benefits={[
          {
            title: "Rewrite weak descriptions faster",
            text: "Instead of rewriting everything manually, Hookora gives you a clearer starting point and reduces friction in your workflow."
          },
          {
            title: "Make your message easier to understand",
            text: "A stronger description helps your audience understand the point of your content faster and more clearly."
          },
          {
            title: "Support better overall content packaging",
            text: "Your description works best when it fits the title, hook, caption, and script. Hookora helps that message feel more connected."
          },
          {
            title: "Improve confidence when publishing",
            text: "Many creators know their content is good but still struggle to describe it well. Hookora helps reduce that hesitation."
          },
          {
            title: "Useful for social posts and video content",
            text: "Whether you are writing for YouTube, short-form content, or broader creator workflows, stronger descriptions improve presentation."
          },
          {
            title: "Stay more consistent across repeated output",
            text: "If you create often, this tool helps keep your messaging cleaner and more repeatable across multiple posts or uploads."
          }
        ]}
      />

      <ToolSeoFAQ
        title="Description Rewriter FAQ"
        intro="Here are a few common questions about Hookora’s Description Rewriter."
        faqs={descriptionFaqs}
      />

      <ToolSeoCTA
        badge="Start free with Hookora"
        title="Use Hookora’s Description Rewriter to improve your message with less effort"
        description="If you want clearer descriptions, stronger content positioning, and a smoother workflow, Hookora gives you a practical place to start."
        primaryLabel="Create Free Account"
        primaryTo="/register"
        secondaryLabel="Explore Features"
        secondaryTo="/features"
      />
    </div>
  );
}

export default DescriptionRewriterPage;